import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, Star, MapPin, BadgeCheck, Crown, ChevronDown, Clock4, Loader2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { FORM_OPTIONS } from "../lib/mockData";
import { api } from "../lib/api";

export default function Attorneys() {
  const [q, setQ] = useState("");
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { setProviders(await api.listAttorneys()); }
      catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() =>
    providers.filter(p => {
      const name = (p.firm_name || p.name || "").toLowerCase();
      const specs = (p.specializations || []).join(" ").toLowerCase();
      return !q || name.includes(q.toLowerCase()) || specs.includes(q.toLowerCase());
    })
  , [q, providers]);

  return (
    <div className="surface-cream min-h-screen" data-testid="attorneys-page">
      <Header/>

      {/* Hero */}
      <section className="bg-white border-b border-[#E1E8E2] py-14 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] blob bg-[#7CC242]"/>
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8">
          <div className="chip mb-4">Directory</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#0A2520] mb-3 leading-tight">All Patent Attorneys & IP Firms</h1>
          <p className="text-lg text-[#5A6B66] max-w-2xl mb-7">Browse 850+ verified patent attorneys and IP law firms across India. Filter by specialty, city, language, and pricing.</p>
          <div className="flex flex-wrap gap-3 max-w-2xl">
            <div className="relative flex-1 min-w-[260px]">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5A6B66]"/>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, specialty, city…" className="input pl-11" data-testid="attorneys-search"/>
            </div>
            <Link to="/attorneys/premium" className="btn-accent"><Crown size={14}/> View Premium only</Link>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10 grid lg:grid-cols-[260px_1fr] gap-8">
        {/* Filters */}
        <aside className="card-flat p-5 h-fit lg:sticky lg:top-[90px]" data-testid="attorneys-filters">
          <div className="flex items-center justify-between mb-5">
            <div className="font-display font-semibold text-[#0A2520] flex items-center gap-2"><Filter size={16}/> Filters</div>
            <button className="text-xs text-[#0E6E5E] font-semibold">Reset</button>
          </div>

          <Section title="Type"><Radio label="All firms" checked/><Radio label="Premium only"/><Radio label="Verified only"/></Section>
          <Section title="Location">{FORM_OPTIONS.cities.slice(0, 6).map((c, i) => <CB key={c} label={c} checked={i < 3}/>)}</Section>
          <Section title="Specialization">{["AI/ML","Software","Biotech","Pharma","Mechanical","Defence"].map((s, i) => <CB key={s} label={s} checked={i < 2}/>)}</Section>
          <Section title="Language">{FORM_OPTIONS.languages.slice(0, 5).map((l, i) => <CB key={l} label={l} checked={i < 2}/>)}</Section>
          <Section title="Price From">
            <div className="space-y-2">
              {["< ₹10,000","₹10,000 – ₹20,000","₹20,000 – ₹30,000","₹30,000+"].map((p, i) => <CB key={p} label={p} checked={i === 1}/>)}
            </div>
          </Section>
          <Section title="Rating">
            <div className="flex gap-1">
              {[3, 4, 4.5, 5].map((r) => (
                <button key={r} className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold ${r === 4.5 ? "bg-[#0E6E5E] text-white" : "border border-[#E1E8E2] text-[#5A6B66]"}`}>{r}+</button>
              ))}
            </div>
          </Section>
        </aside>

        {/* Results */}
        <main>
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="text-sm text-[#5A6B66]">Showing <span className="font-semibold text-[#0A2520]">{filtered.length} of 850+</span> attorneys</div>
            <button className="btn-outline text-sm">Sort: Top rated <ChevronDown size={14}/></button>
          </div>

          <div className="space-y-4">
            {loading && <div className="card p-8 text-center text-[#5A6B66]"><Loader2 className="inline animate-spin mr-2"/> Loading attorneys…</div>}
            {!loading && filtered.length === 0 && <div className="card p-8 text-center text-[#5A6B66]">No attorneys match your search.</div>}
            {filtered.map((p) => (
              <Link to={`/attorneys/${p.id}`} key={p.id} className="card p-6 block relative" data-testid={`attorney-card-${p.id}`}>
                {p.premium && <div className="absolute top-4 right-4 chip chip-gold"><Crown size={10}/> Premium</div>}
                <div className="grid md:grid-cols-[auto_1fr_auto] gap-6">
                  <img src={p.avatar} alt={p.firm_name || p.name} className="w-20 h-20 rounded-2xl object-cover"/>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className="font-display text-xl font-bold text-[#0A2520]">{p.firm_name || p.name}</span>
                      <span className="chip inline-flex items-center gap-1"><BadgeCheck size={11}/> Verified</span>
                    </div>
                    <div className="text-sm text-[#5A6B66] mb-3">{p.tagline} · <MapPin size={11} className="inline -mt-0.5"/> {p.location || p.city}</div>
                    <div className="flex items-center gap-3 mb-3 text-sm flex-wrap">
                      <span className="flex items-center gap-1 font-semibold"><Star size={13} className="fill-[#7CC242] text-[#7CC242]"/>{p.rating || "—"} <span className="text-[#5A6B66] font-normal">({p.reviews || 0})</span></span>
                      <span className="text-[#5A6B66]">·</span>
                      <span className="text-[#5A6B66]">{(p.patents_filed || 0).toLocaleString()} patents filed</span>
                      <span className="text-[#5A6B66]">·</span>
                      <span className="text-[#5A6B66] flex items-center gap-1"><Clock4 size={11}/> {p.turnaround || "—"}</span>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {(p.specializations || []).map((s) => <span key={s} className="chip">{s}</span>)}
                    </div>
                  </div>
                  <div className="md:text-right md:min-w-[160px] flex md:flex-col items-start md:items-end gap-3 pt-3 md:pt-0 border-t md:border-t-0 md:border-l border-[#F0F4F0] md:pl-6">
                    <div>
                      <div className="text-xs text-[#5A6B66]">Starting</div>
                      <div className="font-display text-2xl font-extrabold text-[#0A2520]">₹{(p.price_from || 0).toLocaleString('en-IN')}</div>
                    </div>
                    <button className="btn-primary text-sm md:w-full justify-center">View Profile</button>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="flex justify-center items-center gap-2 mt-10">
            <button className="btn-outline text-sm">← Previous</button>
            <button className="w-9 h-9 rounded-full bg-[#0E6E5E] text-white text-sm font-bold">1</button>
            <button className="w-9 h-9 rounded-full border border-[#E1E8E2] text-[#5A6B66] text-sm font-semibold hover:border-[#0E6E5E]">2</button>
            <button className="w-9 h-9 rounded-full border border-[#E1E8E2] text-[#5A6B66] text-sm font-semibold hover:border-[#0E6E5E]">3</button>
            <span className="text-[#5A6B66] px-2">…</span>
            <button className="w-9 h-9 rounded-full border border-[#E1E8E2] text-[#5A6B66] text-sm font-semibold hover:border-[#0E6E5E]">94</button>
            <button className="btn-outline text-sm">Next →</button>
          </div>
        </main>
      </div>

      {/* Premium upsell */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-16">
        <div className="card-flat p-8 lg:p-10 grid lg:grid-cols-[2fr_auto] gap-6 items-center bg-gradient-to-br from-[#FCF6E2] to-white border-[#F5D442]/30">
          <div>
            <div className="chip chip-gold mb-3"><Crown size={12}/> For attorneys</div>
            <h3 className="font-display font-extrabold text-3xl text-[#0A2520] mb-2">List your firm. Get 4× more leads.</h3>
            <p className="text-[#5A6B66] leading-relaxed max-w-xl">Premium Profile firms get top placement in search, dedicated lead routing, and unlimited high-intent innovator briefs. Featured across the marketplace.</p>
          </div>
          <Link to="/attorneys/premium" className="btn-dark">Become Premium</Link>
        </div>
      </section>

      <Footer/>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5 pb-5 border-b border-[#F0F4F0] last:border-0 last:mb-0 last:pb-0">
      <div className="font-display text-xs font-bold text-[#0A2520] uppercase tracking-wider mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function CB({ label, checked }) {
  return (
    <label className="flex items-center gap-2.5 text-sm cursor-pointer group">
      <div className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? "bg-[#0E6E5E] border-[#0E6E5E]" : "border-[#DDE5DD] group-hover:border-[#0E6E5E]"}`}>
        {checked && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M5 12l5 5L20 7"/></svg>}
      </div>
      <span className={checked ? "text-[#0A2520] font-medium" : "text-[#5A6B66]"}>{label}</span>
    </label>
  );
}

function Radio({ label, checked }) {
  return (
    <label className="flex items-center gap-2.5 text-sm cursor-pointer group">
      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${checked ? "border-[#0E6E5E]" : "border-[#DDE5DD] group-hover:border-[#0E6E5E]"}`}>
        {checked && <div className="w-2 h-2 rounded-full bg-[#0E6E5E]"/>}
      </div>
      <span className={checked ? "text-[#0A2520] font-medium" : "text-[#5A6B66]"}>{label}</span>
    </label>
  );
}
